import styled, { keyframes } from "styled-components";
import { Player } from "@lottiefiles/react-lottie-player";
import { Text, ColorV2 } from "@adoptaunabuelo/react-components";
import animation from "../../../assets/animations/heart.json";
import SectionContainer from "../../../components/container/SectionContainer";
import { LetterScheme, UserScheme } from "client-v2";
import { State } from "../../../redux";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import FooterSocialMedia from "../../../components/section/success/FooterSocialMedia";
import ShareIGSection, {
	ShareIGSectionProps,
} from "../../../components/section/success/ShareIGSection";
import SuccessCrossSell from "./SuccessCrossSell";
import media from "styled-media-query";
import { ChevronDown, MoveDiagonal } from "lucide-react";
import ReliefModal from "./ReliefModal";
import VideoModal from "../../modal/VideoModal";

const labelFadeIn = keyframes`
  	from {
    	opacity: 0;
    	transform: translateY(30px);
  	}
  	to {
    	opacity: 1;
    	transform: translateY(0);
  	}
`;

const StepContainer = styled.div`
	display: flex;
	flex: 1;
	width: 100%;
	flex-direction: column;
	margin-bottom: 32px;
`;
const Col = styled.div`
	display: flex;
	flex-direction: column;
`;
const Row = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
`;
const ArrowAnimationView = styled.div`
	display: none;
	position: sticky;
	bottom: 0px;
	right: -24px;
	left: -24px;
	margin: 0px -24px;
	width: 100%;
	padding: 52px 24px 16px;
	background: linear-gradient(0deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
	align-items: flex-end;
	justify-content: center;
	${media.lessThan("medium")`
		display: flex;
    `}
`;
const ThankUVideoView = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 16px;
	${media.lessThan("medium")`
		margin-top: 48px;
    `}
`;
const ThankUVideoButton = styled.div`
	position: absolute;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 96px;
	width: 96px;
	border-radius: 96px;
	cursor: pointer;
	background-color: rgba(0, 0, 0, 0);
	opacity: 0;
	&:hover {
		background-color: rgba(0, 0, 0, 0.5);
		opacity: 1;
	}
`;
const ThankUVideoLabel = styled.div<{ visible: boolean }>`
	position: absolute;
	background-color: ${ColorV2.surface.primaryHard};
	padding: 8px 12px;
	border-radius: 8px;
	top: -48px;
	opacity: ${({ visible }) => (visible ? 1 : 0)};
	animation: ${({ visible }) => (visible ? labelFadeIn : "none")} 0.6s
		ease-out;
	transition: opacity 0.3s ease;
`;
const LabelSpan = styled.span`
	&::before {
		content: "";
		position: absolute;
		width: 0;
		height: 0;
		border-top: 11px solid ${ColorV2.surface.primaryHard};
		border-left: 8px solid transparent;
		border-right: 8px solid transparent;
		top: 100%;
		left: 50%;
		margin-left: -8px;
		margin-top: -1px;
	}
`;
const ThankUVideo = styled.video`
	height: 96px;
	width: 96px;
	border-radius: 96px;
	object-fit: cover;
`;

const Success = ({
	user,
	steps,
	title,
	subtitle,
	didSubscribe,
	thankYouVideo,
	children,
	reliefModalProps,
	...restProps
}: Props & ShareIGSectionProps) => {
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const appLocation = useSelector((state: State) => state.location.location);
	const [showModal, setShowModal] = useState(false);
	const [showVideo, setShowVideo] = useState(false);
	const [labelVisible, setLabelVisible] = useState(false);

	useEffect(() => {
		const timer = setTimeout(() => {
			setLabelVisible(true);
		}, 1000);

		return () => clearTimeout(timer);
	}, []);

	useEffect(() => {
		if (
			appLocation &&
			appLocation.country_code === "ES" &&
			didSubscribe &&
			user &&
			(!user.idCard || !user.zipCode)
		) {
			setTimeout(() => setShowModal(true), 1000);
		}
	}, [didSubscribe]);

	return (
		<>
			{!reliefModalProps?.hide && (
				<ReliefModal
					isVisible={showModal}
					user={user}
					onClose={() => setShowModal(false)}
					{...reliefModalProps}
				/>
			)}
			{thankYouVideo && (
				<VideoModal
					isVisible={showVideo}
					url={thankYouVideo.url}
					onClose={() => setShowVideo(false)}
				/>
			)}
			<SectionContainer
				childrenProps={{
					style: isMobile ? { margin: "0px" } : {},
				}}
			>
				{thankYouVideo ? (
					<ThankUVideoView>
						<ThankUVideo
							src={thankYouVideo.urlSmall}
							autoPlay
							muted
							loop
							playsInline
						/>
						<ThankUVideoButton onClick={() => setShowVideo(true)}>
							<MoveDiagonal
								height={32}
								width={32}
								color={"white"}
							/>
						</ThankUVideoButton>
						<ThankUVideoLabel visible={labelVisible}>
							<Text type="c1" style={{ color: "white" }}>
								{thankYouVideo.label}
							</Text>
							<LabelSpan />
						</ThankUVideoLabel>
					</ThankUVideoView>
				) : (
					<Player
						style={{
							width: isMobile ? 300 : 400,
							height: isMobile ? 300 : 400,
							marginTop: isMobile ? -100 : -140,
							marginBottom: isMobile ? -100 : -140,
						}}
						keepLastFrame={true}
						autoplay={true}
						src={animation}
					/>
				)}
				<Text
					type="h3"
					weight="semibold"
					style={{ textAlign: "center" }}
				>
					{title}
				</Text>
				<Text
					type="p"
					style={{
						marginTop: 16,
						marginBottom: 32,
						textAlign: "center",
					}}
				>
					{subtitle}
				</Text>
				{steps && steps.length > 0 && (
					<>
						<Text
							type="h5"
							weight="semibold"
							style={{ marginBottom: 16 }}
						>
							¿Y ahora qué?
						</Text>
						<StepContainer style={{ gap: 16 }}>
							{steps?.map((step, index) => (
								<Row key={index}>
									<Text
										type="p"
										weight="semibold"
										style={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											border:
												"1px solid " +
												ColorV2.border.neutralHard,
											height: 24,
											width: 24,
											borderRadius: 40,
										}}
									>
										{index + 1}
									</Text>
									<Col style={{ flex: 1 }}>
										<Text type="p" weight="medium">
											{step.title}
										</Text>
										<Text
											type="p2"
											style={{
												color: ColorV2.text
													.neutralMedium,
											}}
										>
											{step.subtitle}
										</Text>
									</Col>
								</Row>
							))}
						</StepContainer>
					</>
				)}
				{children ? children : isMobile ? <SuccessCrossSell /> : null}
				{restProps.type !== "register" && (
					<ShareIGSection
						style={isMobile ? { marginBottom: 32 } : {}}
						{...restProps}
					/>
				)}
				<FooterSocialMedia />
				<ArrowAnimationView>
					<ChevronDown />
				</ArrowAnimationView>
			</SectionContainer>
		</>
	);
};
export default Success;
export interface Props {
	didSubscribe?: string;
	user: UserScheme;
	title: string;
	subtitle: string;
	letter?: LetterScheme;
	thankYouVideo?: {
		url: string;
		urlSmall: string;
		label: string;
	};
	steps?: {
		title: string;
		subtitle: string;
	}[];
	children?: React.ReactElement;
	reliefModalProps?: {
		hide?: boolean;
		hideAddress?: boolean;
		hidePresent?: boolean;
	};
}
